import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import ArrowIcon from "../../../elements/V2/Icons/ArrowIcon"
import Typography, { TypographySize } from "../../../elements/V2/Typography"

import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React from "react"

export type BenefitDriverTabInfo = {
  bodyText: string
  leadText: string
  number?: number
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
}

type TabNumberProps = {
  number: number
  textColor: Color
}

const TabNumber = ({ number, textColor }: TabNumberProps) => (
  <div
    className={classNames(
      "flex size-32 shrink-0 items-center justify-center rounded-full",
      getBackgroundColorClass(textColor)
    )}
  >
    <Typography
      element="span"
      text={String(number)}
      weight="medium"
      size="subscript-sm"
      font="grotesk"
      color={getAccentColor(textColor)}
      leadingNone
    />
  </div>
)

type TabContentVariant = {
  leadTextSize: TypographySize
  bodyTextSize: TypographySize
  bodyTextSpacingClassName: string
}

const TAB_CONTENT_VARIANTS: Record<"desktop" | "mobile", TabContentVariant> = {
  desktop: {
    leadTextSize: "lead-sm",
    bodyTextSize: "body-md",
    bodyTextSpacingClassName: "mt-8",
  },
  mobile: {
    leadTextSize: "body-lg-fixed",
    bodyTextSize: "body-md-fixed",
    bodyTextSpacingClassName: "mt-16",
  },
}

type TabContentProps = BenefitDriverTabInfo & {
  textColor: Color
  variant: "desktop" | "mobile"
}

const TabContent = ({
  textColor,
  bodyText,
  leadText,
  variant,
  number,
  ctaText,
  ctaLink,
}: TabContentProps) => {
  const { leadTextSize, bodyTextSize, bodyTextSpacingClassName } =
    TAB_CONTENT_VARIANTS[variant]

  return (
    <div className="flex items-start gap-12">
      {number !== undefined && (
        <TabNumber number={number} textColor={textColor} />
      )}

      <div className="flex flex-1 flex-col">
        <Typography
          color={textColor}
          font="grotesk"
          weight="medium"
          size={leadTextSize}
          text={leadText}
          className="transition-all duration-500 ease-in-out"
          element="h4"
        />

        <Typography
          text={bodyText}
          font="grotesk"
          size={bodyTextSize}
          weight="book"
          color={textColor}
          element="p"
          className={classNames("opacity-70", bodyTextSpacingClassName)}
        />

        {ctaText && ctaLink && (
          <TextButton
            text={ctaText}
            linkUrl={ctaLink}
            color={Color.Blue}
            style="icon"
            icon={<ArrowIcon />}
            className="mt-16"
          />
        )}
      </div>
    </div>
  )
}

export default TabContent
